import { computed } from '@ember/object';
import { resolve } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import Enum from '@square/dashboard-utilities/utils/enum';
import Walkthrough, { FIXED_BOTTOM_RIGHT } from '@square/onboarding/models/walkthrough';
import WalkthroughStep from '@square/onboarding/models/walkthrough-step';
import { ActionItemTypes } from '@dashboard/shared-constants/setup-guide/action-types';
import { SetUpKDSStepNames, SavedAMenuItemStepNames, SetUpRstGridLayoutStepNames } from '@dashboard/shared-constants/setup-guide/rst/config/walkthrough';
import { SetUpItemsStepNames } from '@dashboard/shared-constants/setup-guide/catalog/config/walkthrough';
import { walkthroughStepCompleted, walkthroughCompleted, actionItemCompletePopoverNext } from '@dashboard/shared-ui/events/setup-guide/event-catalog';
import Item from '@square/dashboard-items-data/models/item';
import { ItemTypesForFiltering_Numeric } from '@square/dashboard-items-data/utils/item-type-constants';
import { getActionItemCompleteName } from '@dashboard/shared-ui/utils/setup-guide/walkthrough';
import { WalkthroughDataAttrValues as PricingWalkthroughDataAttrValues } from '@dashboard/shared-constants/pricing/walkthrough';
import { ModelType as PricingModelType } from '@dashboard/shared-constants/pricing/constants';
import { CreateBarCodeLabelStepNames } from '@dashboard/shared-ui/utils/setup-guide/step-names';
import ActionItemCompletePopover from '@dashboard/shared-ui/components/setup-guide/action-item-complete-popover';
import SetupGuideWalkthroughPopover from '@dashboard/shared-ui/components/setup-guide/walkthrough-popover';
export const AddALocationStepNames = new Enum('ADD_LOCATION', 'ADD_DETAILS', 'SAVE_LOCATION');
export const ActivateTwoFactorAuthStepNames = new Enum('START_HERE');
export const CustomizeReceiptsStepNames = new Enum('CUSTOMIZE_RECEIPT', 'ADD_SOCIAL_PROFILES', 'SAVE_RECEIPT');
export const LinkYourBankAccountStepNames = new Enum('ADD_BANK_ACCOUNT', 'FILL_IN_BANK_ACCOUNT', 'SAVE_BANK_ACCOUNT');
export const ManageEmployeesStepNames = new Enum('EMPLOYEE_LIST', 'ADD_EMPLOYEE', 'ENABLE_PERMISSIONS', 'SAVE_EMPLOYEE');
export const SetUpDevicesStepNames = new Enum('ADD_DEVICE', 'NAME_DEVICE', 'CREATE_DEVICE_CODE', 'COPY_DEVICE_CODE');
export const PairRetailDeviceStepNames = new Enum('PAIR_DEVICE', 'SHOW_DEVICE_CODE');
export const SetUpFloorPlanStepNames = new Enum('CREATE_FLOOR_PLAN', 'SAVE_FLOOR_PLAN', 'ADD_SECTION', 'SAVE_SECTION', 'DRAG_TABLES', 'SAVE_FLOOR_PLAN_CHANGES');
export const SetUpTaxesStepNames = new Enum('CREATE_TAX', 'SAVE_TAX', 'APPLY_TAX');
export const SetUpDiscountsStepNames = new Enum('CREATE_DISCOUNT', 'ADD_DETAILS', 'SAVE_DISCOUNT');
export const SetUpVendorsStepNames = new Enum('CREATE_VENDOR', 'ADD_DETAILS', 'SAVE_VENDOR');
export const SubscribedToRetailStepNames = new Enum('SUBSCRIBE');
export const ViewInvoiceSettingsStepNames = new Enum('SAVE_SETTINGS');

/**
 * Helper service to lookup walkthroughs for action items
 */
// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  catalog: service(),
  eventstream: service(),
  i18n: service(),
  screen: service(),
  setupGuide: service(),
  store: service(),
  translation: service('setup-guide/translation'),
  userInfo: service(),
  userUnits: service(),
  SetUpDiscountsStepNames,
  CreateBarCodeLabelStepNames,
  // eslint-disable-next-line complexity
  getWalkthrough(actionItemName) {
    let walkthrough;
    switch (actionItemName) {
      case ActionItemTypes.ADD_A_LOCATION:
        walkthrough = this.addALocationWalkthrough;
        break;
      case ActionItemTypes.ACTIVATE_TWO_FACTOR_AUTH:
        walkthrough = this.activateTwoFactorAuthWalkthrough;
        break;
      case ActionItemTypes.CREATE_BAR_CODE_LABEL:
        return this.catalog.hasAnyItems({
          productTypes: ItemTypesForFiltering_Numeric,
          enabledUnitTokens: this.userUnits.hasAccessToAllSubunits ? undefined : this.userInfo.authorizedUnitTokens
        }).then(hasItems => {
          if (hasItems) {
            return this.createBarCodeLabelWalkthrough;
          } else {
            return this.createBarCodeLabelWithoutItemsWalkthrough;
          }
        });
      case ActionItemTypes.CUSTOMIZE_INVOICE_RECEIPTS:
      case ActionItemTypes.CUSTOMIZE_RECEIPTS:
        walkthrough = this.customizeReceiptsWalkthrough;
        break;
      case ActionItemTypes.LINK_YOUR_BANK_ACCOUNT:
        walkthrough = this.linkYourBankAccountWalkthrough;
        break;
      case ActionItemTypes.MANAGE_EMPLOYEES:
        walkthrough = this.manageEmployeesWalkthrough;
        break;
      case ActionItemTypes.SAVED_A_MENU_ITEM:
        // MENU here refers to Menu.catalogType()
        return this.catalog.getCount({
          types: ['MENU']
        }).then(menuCount => {
          const walkthrough = this.savedAMenuItemWalkthrough;
          // Change the tooltip of the first step to be bottom-end if there is a menu,
          // otherwise leave it at bottom.
          walkthrough.get('steps')[0].placement = menuCount > 0 ? 'bottom-end' : 'bottom';
          return walkthrough;
        });
      case ActionItemTypes.SET_UP_DEVICES:
        walkthrough = this.setUpDevicesWalkthrough;
        break;
      case ActionItemTypes.SET_UP_KDS:
        walkthrough = this.setUpKDSWalkthrough;
        break;
      case ActionItemTypes.SET_UP_FLOOR_PLAN:
        walkthrough = this.setUpFloorPlanWalkthrough;
        break;
      case ActionItemTypes.SET_UP_ITEMS:
        walkthrough = this.setUpItemsWalkthrough;
        break;
      // TODO(RST-5601) remove references to SET_UP_RST_GRID_LAYOUT
      // after it's removed from Checklist service
      case ActionItemTypes.SET_UP_RST_GRID_LAYOUT:
        walkthrough = this.setUpRstGridLayoutWalkthrough;
        break;
      case ActionItemTypes.SET_UP_TAXES:
        return this.catalog.getCount({
          types: [Item.catalogType()]
        }).then(itemCount => {
          if (itemCount > 0) {
            return this.setUpTaxesWalkthroughWithItems;
          } else {
            return this.setUpTaxesWalkthroughWithoutItems;
          }
        });
      case ActionItemTypes.SET_UP_DISCOUNTS:
        walkthrough = this.setUpDiscountsWalkthrough;
        break;
      case ActionItemTypes.SET_UP_VENDORS:
        walkthrough = this.setUpVendorsWalkthrough;
        break;
      case ActionItemTypes.SUBSCRIBE_TO_RETAIL:
        walkthrough = this.subscribeToRetailWalkthrough;
        break;
      case ActionItemTypes.SET_UP_RETAIL_DEVICES:
        return this.store.findAll('device-credential').then(devices => {
          if (devices.isAny('deviceProfile.settings.type', 'RETAIL')) {
            return this.pairRetailDeviceWalkthrough;
          } else {
            return this.addRetailDeviceWalkthrough;
          }
        });
      case ActionItemTypes.VIEW_INVOICE_SETTINGS:
        walkthrough = this.viewInvoiceSettingsWalkthrough;
        break;
      default:
        break;
    }
    return resolve(walkthrough);
  },
  getActionItemCompleteWalkthrough(actionItemName) {
    const allItemsFinished = this.setupGuide?.allItemsFinished;
    const translationParams = {
      businessName: this.userInfo.name
    };
    return Walkthrough.create({
      popoverComponent: ActionItemCompletePopover,
      name: getActionItemCompleteName(actionItemName),
      steps: [new WalkthroughStep({
        name: actionItemName,
        targetElement: FIXED_BOTTOM_RIGHT,
        text: this.translation.getStepComplete(actionItemName, translationParams),
        subtext: allItemsFinished ? this.i18n.t('setupGuide.complete') : this.translation.getCompleted(actionItemName),
        buttonText: allItemsFinished ? this.i18n.t('button.done') : this.translation.getButtonNext(actionItemName)
      })],
      onComplete: () => {
        this.eventstream.track(actionItemCompletePopoverNext(actionItemName));
        this.setupGuide.awaitSetup().then(() => {
          this.setupGuide.goToNextActionItem();
        });
      }
    });
  },
  buildWalkthrough(actionItemName, steps) {
    return Walkthrough.create({
      name: actionItemName,
      steps,
      popoverComponent: SetupGuideWalkthroughPopover,
      isUserInitiated: true,
      onStepComplete: stepName => {
        this.eventstream.track(walkthroughStepCompleted(actionItemName, stepName));
      },
      onComplete: () => {
        this.eventstream.track(walkthroughCompleted(actionItemName));
      }
    });
  },
  /**
    When creating walkthroughs, make sure to double check the positioning on both desktop and mobile,
    as its likely the tooltip placement will need to differ across the two form factors.
  */

  addALocationWalkthrough: computed('i18n', 'screen.isMobile', function () {
    return this.buildWalkthrough(ActionItemTypes.ADD_A_LOCATION, [new WalkthroughStep({
      name: AddALocationStepNames.ADD_LOCATION,
      targetElement: '[data-walkthrough-locations-list] market-button[rank="primary"]',
      text: this.i18n.t('setupGuide.actionItems.addALocation.walkthrough.addLocation'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: AddALocationStepNames.ADD_DETAILS,
      targetElement: 'market-modal-full .locations-edit--business-name',
      text: this.i18n.t('setupGuide.actionItems.addALocation.walkthrough.addDetails')
    }), new WalkthroughStep({
      name: AddALocationStepNames.SAVE_LOCATION,
      targetElement: 'market-modal-full market-button[rank="primary"]',
      text: this.i18n.t('setupGuide.actionItems.addALocation.walkthrough.saveLocation'),
      placement: this.screen.isMobile ? 'top-middle' : 'bottom-end'
    })]);
  }),
  activateTwoFactorAuthWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.ACTIVATE_TWO_FACTOR_AUTH, [new WalkthroughStep({
      name: ActivateTwoFactorAuthStepNames.START_HERE,
      targetElement: '[data-walkthrough-two-factor] .button--primary',
      text: this.i18n.t('setupGuide.actionItems.activateTwoFactorAuth.walkthrough.startHere')
    })]);
  }),
  createBarCodeLabelWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.CREATE_BAR_CODE_LABEL, [new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.SELECT_PRINT_LABELS,
      targetElement: '[data-item-library-actions-button-dropdown]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.selectPrintLabels'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.SELECT_LABEL_TYPE_AND_LOCATION,
      targetElement: '[data-walkthrough-select-label-type-and-location]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.selectLabelTypeAndLocation'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.SELECT_ITEMS,
      targetElement: '[data-walkthrough-select-items]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.selectItems'),
      placement: 'left-end'
    }), new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.CREATE_LABELS,
      targetElement: '[data-walkthrough-create-labels]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.createLabels'),
      placement: 'bottom-end'
    })]);
  }),
  createBarCodeLabelWithoutItemsWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.CREATE_BAR_CODE_LABEL, [new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.CREATE_ITEM,
      targetElement: '[data-walkthrough-create-item]',
      text: this.i18n.t('setupGuide.actionItems.setUpItems.walkthrough.createItem')
    }), new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.SELECT_PRINT_LABELS,
      targetElement: '[data-item-library-actions-button-dropdown]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.selectPrintLabels'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.SELECT_LABEL_TYPE_AND_LOCATION,
      targetElement: '[data-walkthrough-select-label-type-and-location]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.selectLabelTypeAndLocation'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.SELECT_ITEMS,
      targetElement: '[data-walkthrough-select-items]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.selectItems'),
      placement: 'bottom-start'
    }), new WalkthroughStep({
      name: CreateBarCodeLabelStepNames.CREATE_LABELS,
      targetElement: '[data-walkthrough-create-labels]',
      text: this.i18n.t('setupGuide.actionItems.createBarCodeLabel.walkthrough.createLabels'),
      placement: 'bottom-end'
    })]);
  }),
  customizeReceiptsWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.CUSTOMIZE_RECEIPTS, [new WalkthroughStep({
      name: CustomizeReceiptsStepNames.CUSTOMIZE_RECEIPT,
      targetElement: '.l-receipt-editor__color-swatches',
      text: this.i18n.t('setupGuide.actionItems.customizeReceipts.walkthrough.customizeReceipt')
    }), new WalkthroughStep({
      name: CustomizeReceiptsStepNames.ADD_SOCIAL_PROFILES,
      targetElement: '[data-walkthrough-receipt-social-profiles]',
      text: this.i18n.t('setupGuide.actionItems.customizeReceipts.walkthrough.addSocialProfiles'),
      placement: 'top-end'
    }), new WalkthroughStep({
      name: CustomizeReceiptsStepNames.SAVE_RECEIPT,
      targetElement: '.navbar-toolbar .button--primary:enabled',
      text: this.i18n.t('setupGuide.actionItems.customizeReceipts.walkthrough.saveReceipt'),
      placement: 'top-end'
    })]);
  }),
  linkYourBankAccountWalkthrough: computed('i18n', 'screen.isMobile', function () {
    return this.buildWalkthrough(ActionItemTypes.LINK_YOUR_BANK_ACCOUNT, [new WalkthroughStep({
      name: LinkYourBankAccountStepNames.ADD_BANK_ACCOUNT,
      targetElement: '[data-walkthrough-add-bank-account]',
      text: this.i18n.t('setupGuide.actionItems.linkYourBankAccount.walkthrough.addBankAccount'),
      placement: this.screen.isMobile ? 'bottom-start' : 'bottom-end'
    }), new WalkthroughStep({
      name: LinkYourBankAccountStepNames.FILL_IN_BANK_ACCOUNT,
      targetElement: '[data-walkthrough-fill-in-bank-account]',
      text: this.i18n.t('setupGuide.actionItems.linkYourBankAccount.walkthrough.fillInBankAccount')
    }), new WalkthroughStep({
      name: LinkYourBankAccountStepNames.SAVE_BANK_ACCOUNT,
      targetElement: '.change-bank-account-modal .button--primary',
      text: this.i18n.t('setupGuide.actionItems.linkYourBankAccount.walkthrough.saveBankAccount'),
      placement: this.screen.isMobile ? 'top-middle' : 'bottom-end'
    })]);
  }),
  manageEmployeesWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.MANAGE_EMPLOYEES, [new WalkthroughStep({
      name: ManageEmployeesStepNames.ADD_EMPLOYEE,
      targetElement: '[data-walkthrough-add-employee]',
      text: this.i18n.t('setupGuide.actionItems.manageEmployees.walkthrough.addEmployee')
    })]);
  }),
  setUpDevicesWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_DEVICES, [new WalkthroughStep({
      name: SetUpDevicesStepNames.ADD_DEVICE,
      targetElement: '[data-walkthrough-add-device]',
      text: this.i18n.t('setupGuide.actionItems.setUpDevices.walkthrough.addDevice')
    }), new WalkthroughStep({
      name: SetUpDevicesStepNames.NAME_DEVICE,
      targetElement: '[data-walkthrough-device-details]',
      text: this.i18n.t('setupGuide.actionItems.setUpDevices.walkthrough.nameDevice')
    }), new WalkthroughStep({
      name: SetUpDevicesStepNames.CREATE_DEVICE_CODE,
      targetElement: '.l-device-credential__sheet .button--primary',
      text: this.i18n.t('setupGuide.actionItems.setUpDevices.walkthrough.createDeviceCode'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SetUpDevicesStepNames.COPY_DEVICE_CODE,
      targetElement: '.l-device-credential__sheet .button--primary',
      text: this.i18n.t('setupGuide.actionItems.setUpDevices.walkthrough.copyDeviceCode'),
      placement: 'bottom-end'
    })]);
  }),
  // TODO(dpark): RST-15958 - Auto-complete if KDS already created before
  setUpKDSWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_KDS, [new WalkthroughStep({
      name: SetUpKDSStepNames.ADD_KDS_DEVICE,
      targetElement: '[data-walkthrough-add-device]',
      text: this.i18n.t('setupGuide.actionItems.setUpKDS.walkthrough.addDevice')
    }), new WalkthroughStep({
      name: SetUpKDSStepNames.NAME_KDS_DEVICE,
      targetElement: '[data-walkthrough-set-up-kds-name-device]',
      text: this.i18n.t('setupGuide.actionItems.setUpKDS.walkthrough.nameDevice')
    }), new WalkthroughStep({
      name: SetUpKDSStepNames.CREATE_KDS_DEVICE_CODE,
      targetElement: '[data-walkthrough-set-up-kds-create-device-code]',
      text: this.i18n.t('setupGuide.actionItems.setUpKDS.walkthrough.createDeviceCode'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SetUpKDSStepNames.COPY_KDS_DEVICE_CODE,
      targetElement: '[data-walkthrough-set-up-kds-copy-device-code]',
      text: this.i18n.t('setupGuide.actionItems.setUpKDS.walkthrough.copyDeviceCode'),
      placement: 'bottom-end'
    })]);
  }),
  addRetailDeviceWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_RETAIL_DEVICES, [new WalkthroughStep({
      name: SetUpDevicesStepNames.ADD_DEVICE,
      targetElement: '[data-walkthrough-add-device]',
      text: this.i18n.t('setupGuide.actionItems.setUpRetailDevices.walkthrough.addDevice'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SetUpDevicesStepNames.NAME_DEVICE,
      targetElement: '[data-walkthrough-device-details] .form-field__content',
      text: this.i18n.t('setupGuide.actionItems.setUpRetailDevices.walkthrough.nameDevice'),
      placement: 'top-center'
    }), new WalkthroughStep({
      name: SetUpDevicesStepNames.CREATE_DEVICE_CODE,
      targetElement: '.l-device-credential__sheet .button--primary',
      text: this.i18n.t('setupGuide.actionItems.setUpRetailDevices.walkthrough.createDeviceCode'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SetUpDevicesStepNames.COPY_DEVICE_CODE,
      targetElement: '.device-code-vertical__password',
      text: this.i18n.t('setupGuide.actionItems.setUpRetailDevices.walkthrough.sendDeviceCode'),
      placement: 'top-center'
    })]);
  }),
  subscribeToRetailWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SUBSCRIBE_TO_RETAIL, [new WalkthroughStep({
      name: SubscribedToRetailStepNames.SUBSCRIBE,
      targetElement: '[data-walkthrough-subscribe-to-retail]',
      text: this.i18n.t('setupGuide.actionItems.subscribeToRetail.walkthrough.subscribe'),
      placement: 'bottom-end'
    })]);
  }),
  pairRetailDeviceWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_RETAIL_DEVICES, [new WalkthroughStep({
      name: PairRetailDeviceStepNames.PAIR_DEVICE,
      targetElement: '[data-walkthrough-pair-retail-device]',
      text: this.i18n.t('setupGuide.actionItems.setUpRetailDevices.walkthrough.pairDevice'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: PairRetailDeviceStepNames.SHOW_DEVICE_CODE,
      targetElement: '.l-device-credential-edit-device__input-code',
      text: this.i18n.t('setupGuide.actionItems.setUpRetailDevices.walkthrough.showDeviceCode'),
      placement: 'bottom-start'
    })]);
  }),
  setUpItemsWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_ITEMS, [new WalkthroughStep({
      name: SetUpItemsStepNames.CREATE_ITEM,
      targetElement: '[data-walkthrough-create-item]',
      text: this.i18n.t('setupGuide.actionItems.setUpItems.walkthrough.createItem')
    }), new WalkthroughStep({
      name: SetUpItemsStepNames.NAME_ITEM,
      targetElement: '.l-item-library-edit-item-name',
      text: this.i18n.t('setupGuide.actionItems.setUpItems.walkthrough.nameItem'),
      placement: 'bottom-start'
    }), new WalkthroughStep({
      name: SetUpItemsStepNames.ASSIGN_CATEGORY,
      targetElement: '[data-item-library-advanced-categorization-select-categories]',
      text: this.i18n.t('setupGuide.actionItems.setUpItems.walkthrough.assignCategory'),
      placement: 'right-start'
    }), new WalkthroughStep({
      name: SetUpItemsStepNames.ENTER_PRICE,
      targetElement: '.item-variation__price',
      text: this.i18n.t('setupGuide.actionItems.setUpItems.walkthrough.enterPrice'),
      placement: 'bottom-start'
    }), new WalkthroughStep({
      name: SetUpItemsStepNames.CUSTOMIZE_ITEM,
      targetElement: '.l-item-library-edit-preview-image-uploader',
      text: this.i18n.t('setupGuide.actionItems.setUpItems.walkthrough.customizeItem'),
      placement: 'bottom-start'
    }), new WalkthroughStep({
      name: SetUpItemsStepNames.SAVE_ITEM,
      targetElement: '.walkthrough-item-edit-sheet-save',
      text: this.i18n.t('setupGuide.actionItems.setUpItems.walkthrough.saveItem'),
      placement: 'left-start'
    })]);
  }),
  setUpDiscountsWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_DISCOUNTS, [new WalkthroughStep({
      name: SetUpDiscountsStepNames.CREATE_DISCOUNT,
      targetElement: '[data-walkthrough-create-discount]',
      text: this.i18n.t('setupGuide.actionItems.setUpDiscounts.walkthrough.createDiscount')
    }), new WalkthroughStep({
      name: SetUpDiscountsStepNames.ADD_DETAILS,
      targetElement: `[data-walkthrough-pricing-name-field="${PricingWalkthroughDataAttrValues[PricingModelType.DISCOUNT].nameField}"]`,
      text: this.i18n.t('setupGuide.actionItems.setUpDiscounts.walkthrough.addDetails'),
      placement: 'bottom-center'
    }), new WalkthroughStep({
      name: SetUpDiscountsStepNames.SAVE_DISCOUNT,
      targetElement: `[data-walkthrough-pricing-save-button="${PricingWalkthroughDataAttrValues[PricingModelType.DISCOUNT].saveButton}"]`,
      text: this.i18n.t('setupGuide.actionItems.setUpDiscounts.walkthrough.saveDiscount'),
      placement: 'bottom-end'
    })]);
  }),
  setUpVendorsWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_VENDORS, [new WalkthroughStep({
      name: SetUpVendorsStepNames.CREATE_VENDOR,
      targetElement: '[data-walkthrough-create-vendor]',
      text: this.i18n.t('setupGuide.actionItems.setUpVendors.walkthrough.createVendor'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SetUpVendorsStepNames.ADD_DETAILS,
      targetElement: '[data-walkthrough-vendor-details]',
      text: this.i18n.t('setupGuide.actionItems.setUpVendors.walkthrough.addDetails'),
      placement: 'top-center'
    }), new WalkthroughStep({
      name: SetUpVendorsStepNames.SAVE_VENDOR,
      targetElement: '[data-walkthrough-save-vendor]',
      text: this.i18n.t('setupGuide.actionItems.setUpVendors.walkthrough.saveVendor'),
      placement: 'bottom-end'
    })]);
  }),
  setUpTaxesWalkthroughWithoutItems: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_TAXES, [new WalkthroughStep({
      name: SetUpTaxesStepNames.SAVE_TAX,
      targetElement: '[data-walkthrough-save-tax]',
      text: this.i18n.t('setupGuide.actionItems.setUpTaxes.walkthrough.saveTax'),
      placement: 'bottom-end'
    })]);
  }),
  setUpTaxesWalkthroughWithItems: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_TAXES, [new WalkthroughStep({
      name: SetUpTaxesStepNames.SAVE_TAX,
      targetElement: '[data-walkthrough-save-tax]',
      text: this.i18n.t('setupGuide.actionItems.setUpTaxes.walkthrough.saveTax'),
      placement: 'bottom-end'
    })]);
  }),
  savedAMenuItemWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SAVED_A_MENU_ITEM, [new WalkthroughStep({
      name: SavedAMenuItemStepNames.CREATE_MENU,
      targetElement: '[data-walkthrough-create-menu]',
      text: this.i18n.t('setupGuide.actionItems.savedAMenuItem.walkthrough.createMenu'),
      // This attribute is overridden in getWalkthrough#ActionItemTypes.SAVED_A_MENU_ITEM in order
      // to accommodate both the null and non-null Menu states.
      placement: 'bottom'
    }), new WalkthroughStep({
      name: SavedAMenuItemStepNames.SAVE_MENU,
      targetElement: '.menu-details-modal .button--primary:enabled',
      text: this.i18n.t('setupGuide.actionItems.savedAMenuItem.walkthrough.saveMenu'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SavedAMenuItemStepNames.ADD_DISPLAY_GROUP,
      targetElement: '[data-walkthrough-add-display-group]',
      text: this.i18n.t('setupGuide.actionItems.savedAMenuItem.walkthrough.addDisplayGroup'),
      placement: 'right-start'
    }), new WalkthroughStep({
      name: SavedAMenuItemStepNames.SAVE_DISPLAY_GROUP,
      targetElement: '.menu-group-details-modal .button--primary:enabled',
      text: this.i18n.t('setupGuide.actionItems.savedAMenuItem.walkthrough.saveDisplayGroup'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SavedAMenuItemStepNames.ADD_ITEM,
      targetElement: '[data-walkthrough-add-item]',
      text: this.i18n.t('setupGuide.actionItems.savedAMenuItem.walkthrough.addItem'),
      placement: 'right-start'
    }), new WalkthroughStep({
      name: SavedAMenuItemStepNames.SAVE_ITEM,
      targetElement: '.item-details-modal .button--primary:enabled',
      text: this.i18n.t('setupGuide.actionItems.savedAMenuItem.walkthrough.saveItem'),
      placement: 'right-end'
    }), new WalkthroughStep({
      name: SetUpRstGridLayoutStepNames.GRID_LAYOUT,
      targetElement: '[data-walkthrough-grid-layout]',
      text: this.i18n.t('setupGuide.actionItems.setUpRstGridLayout.walkthrough.gridLayout'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SetUpRstGridLayoutStepNames.MODIFY_LAYOUT,
      targetElement: '[data-walkthrough-modify-layout] .menu-grid-element',
      text: this.i18n.t('setupGuide.actionItems.setUpRstGridLayout.walkthrough.modifyLayout'),
      placement: 'bottom-start'
    }), new WalkthroughStep({
      name: SetUpRstGridLayoutStepNames.SAVE_LAYOUT,
      targetElement: '.menu-layout-editor-sheet .button--primary:enabled',
      text: this.i18n.t('setupGuide.actionItems.setUpRstGridLayout.walkthrough.saveLayout'),
      placement: 'bottom-end'
    })]);
  }),
  setUpFloorPlanWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.SET_UP_FLOOR_PLAN, [new WalkthroughStep({
      name: SetUpFloorPlanStepNames.CREATE_FLOOR_PLAN,
      targetElement: '[data-walkthrough-create-floor-plan]',
      text: this.i18n.t('setupGuide.actionItems.setUpFloorPlan.walkthrough.createFloorPlan')
    }), new WalkthroughStep({
      name: SetUpFloorPlanStepNames.SAVE_FLOOR_PLAN,
      targetElement: '.floor-plan-details-modal .button--primary:enabled',
      text: this.i18n.t('setupGuide.actionItems.setUpFloorPlan.walkthrough.saveFloorPlan'),
      placement: 'bottom-end'
    }), new WalkthroughStep({
      name: SetUpFloorPlanStepNames.ADD_SECTION,
      targetElement: '[data-walkthrough-add-section]',
      text: this.i18n.t('setupGuide.actionItems.setUpFloorPlan.walkthrough.addSection'),
      placement: 'left-end'
    }), new WalkthroughStep({
      name: SetUpFloorPlanStepNames.SAVE_SECTION,
      targetElement: '.floor-plan-ticket-group-editor-modal .button--primary:enabled',
      text: this.i18n.t('setupGuide.actionItems.setUpFloorPlan.walkthrough.saveSection'),
      placement: 'right-end'
    }), new WalkthroughStep({
      name: SetUpFloorPlanStepNames.DRAG_TABLES,
      targetElement: '[data-walkthrough-drag-tables]',
      text: this.i18n.t('setupGuide.actionItems.setUpFloorPlan.walkthrough.dragTables'),
      placement: 'left-start'
    }), new WalkthroughStep({
      name: SetUpFloorPlanStepNames.SAVE_FLOOR_PLAN_CHANGES,
      targetElement: '.floor-plan-editor-sheet .button--primary',
      text: this.i18n.t('setupGuide.actionItems.setUpFloorPlan.walkthrough.saveFloorPlanChanges'),
      placement: 'bottom-end'
    })]);
  }),
  viewInvoiceSettingsWalkthrough: computed('i18n', function () {
    return this.buildWalkthrough(ActionItemTypes.VIEW_INVOICE_SETTINGS, [new WalkthroughStep({
      name: ViewInvoiceSettingsStepNames.SAVE_SETTINGS,
      targetElement: '[data-walkthrough-save-invoice-settings]',
      text: this.i18n.t('setupGuide.actionItems.viewInvoiceSettings.walkthrough.saveSettings'),
      placement: 'top-end'
    })]);
  })
});